import { useCachedResource } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import {
  deriveRideStatus,
  expectedJudgeCountForClass,
  expectedSectionCountForClass,
  filterAuthoritativeScoreSheets,
  isPanelConsensusTest,
} from "score-common";
import {
  classes as classesCache,
  getAll,
  rides as ridesCache,
  tests as testsCache,
} from "../local/db";

// #2972 - same contender-exclusion as useRidesForClass's groupSheetsByRide:
// isClassComplete's status derivation must never count a same-seat contender
// (#2815) toward the class's expected judge count. Exported for direct unit
// testing, same reason withStatus below is.
export const groupSheetsByRide = (sheets) => {
  const map = {};
  for (const s of filterAuthoritativeScoreSheets(sheets)) (map[s.rideId] ??= []).push(s);
  return map;
};

// Derived `status` only — this map exists to answer isClassComplete(rides),
// not to render a ride card, so it skips useRidesForClass's horse/rider
// denormalization on purpose. #1804 — testsById is still needed, though: a
// panel-consensus ride's one co-signed sheet must resolve to FINAL here too,
// or a finished Medal/equitation class never reads complete for next-class
// navigation (isClassComplete), same gap #1763 fixed on the ride card.
export function withStatus(klass, rideRows, sheetsByRide, testsById = {}) {
  const expectedJudgeCount = expectedJudgeCountForClass(klass);
  const sectionJudge = expectedSectionCountForClass(klass) != null;
  return rideRows.map((r) => {
    const consensus = isPanelConsensusTest(testsById[r.testId] ?? null);
    return {
      ...r,
      status: deriveRideStatus(r, sheetsByRide[r.id] ?? [], expectedJudgeCount, {
        sectionJudge,
        consensus,
      }),
    };
  });
}

/**
 * #856 — rides for EVERY class in `classIds`, keyed by classId, each ride
 * carrying its derived `status`. Exists because nextAssignedClass needs to
 * check isClassComplete on classes it hasn't resolved as "next" yet — a
 * chicken-and-egg useRidesForClass(nextClass?.id) alone can't answer (it only
 * ever fetches the one class already picked). Same cache-first tables
 * useRidesForClass reads (classes/rides/scoreSheets caches, #421); this is the
 * bulk sibling, fanned out over the assigned list instead of one class, so no
 * new API surface is introduced.
 *
 * @param {string[]|null} classIds
 * @returns {{ data: Record<string, object[]>|null, loading, error }}
 */
export function useRidesByClassId(classIds) {
  const api = useScoreApi();
  const { online } = useScoreContext();
  const ids = classIds ?? [];
  const key = ids.slice().sort().join(",");
  return useCachedResource(
    async () => {
      if (!ids.length) return {};
      const sheetsByRide = groupSheetsByRide(await getAll("scoreSheets"));
      const entries = await Promise.all(
        ids.map(async (id) => {
          const klass = (await classesCache.get(id)) ?? null;
          const rideRows = await ridesCache.byClass(id);
          // #1804 — each ride's OWN Test (never klass.testId, per #435), once
          // per unique id.
          const testsById = {};
          for (const tid of new Set(rideRows.map((r) => r.testId).filter(Boolean))) {
            testsById[tid] = (await testsCache.get(tid)) ?? null;
          }
          return [id, withStatus(klass, rideRows, sheetsByRide, testsById)];
        }),
      );
      return Object.fromEntries(entries);
    },
    async (signal) => {
      if (!ids.length) return {};
      const entries = await Promise.all(
        ids.map(async (id) => {
          const [klass, rideRows] = await Promise.all([
            api.classes.getClass(id, { signal }).then(unwrap).catch(() => null),
            api.rides
              .listRides(id, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, { signal })
              .then(unwrap)
              .catch(() => []),
          ]);
          // #1804 — same testId resolution as useRidesForClass's online path.
          const testsById = {};
          await Promise.all(
            [...new Set(rideRows.map((r) => r.testId).filter(Boolean))].map(async (tid) => {
              testsById[tid] = await api.tests.getTest(tid, { signal }).then(unwrap).catch(() => null);
            }),
          );
          // #1961 — one batched call per class's rides instead of one
          // listScoreSheets HTTP round trip per ride.
          const rideIds = rideRows.map((r) => r.id);
          const sheets = rideIds.length
            ? await api.scoreSheets
                .listScoreSheetsForRides({ rideIds }, { signal })
                .then(unwrap)
                .catch(() => [])
            : [];
          const sheetsByRide = groupSheetsByRide(sheets);
          return [id, withStatus(klass, rideRows, sheetsByRide, testsById)];
        }),
      );
      return Object.fromEntries(entries);
    },
    [api, key],
    online,
  );
}
